'use client';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Search,
  Target,
  FileSearch,
  Scale,
  Clock,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
} from 'lucide-react';

interface Challenge {
  icon: React.ReactNode;
  title: string;
  description: string;
  solution: string;
}

const investorChallenges: Challenge[] = [
  {
    icon: <Search className="w-6 h-6" />,
    title: 'Overwhelming Deal Flow',
    description: 'Poor signal-to-noise ratio makes it difficult to identify quality opportunities',
    solution: 'Curated, pre-vetted deal flow with structured analysis',
  },
  {
    icon: <FileSearch className="w-6 h-6" />,
    title: 'Lack of Research Support',
    description: 'No structured research and analysis support for investment decisions',
    solution: 'On-demand diligence and expert research support',
  },
  {
    icon: <Target className="w-6 h-6" />,
    title: 'Investment Thesis Validation',
    description: 'Difficulty building and validating a coherent investment thesis',
    solution: 'Investor Thesis development with structured frameworks',
  },
  {
    icon: <Scale className="w-6 h-6" />,
    title: 'Independent Evaluation',
    description: 'Need for unbiased startup evaluation beyond founder narratives',
    solution: 'Independent analysis and diligence reports with verified data',
  },
  {
    icon: <Clock className="w-6 h-6" />,
    title: 'Time Constraints',
    description: 'Severe time limitations preventing deep, parallel due diligence',
    solution: 'Parallel diligence support with expert consultants',
  },
];

function ChallengeCard({
  challenge,
  index,
  isVisible,
}: {
  challenge: Challenge;
  index: number;
  isVisible: boolean;
}) {
  return (
    <div
      className={cn(
        'group p-6 rounded-2xl border border-gray-100 bg-white hover:border-purple-200 hover:shadow-lg transition-all duration-500',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Icon */}
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center text-amber-600 mb-4 group-hover:scale-110 transition-transform">
        {challenge.icon}
      </div>

      {/* Challenge Title */}
      <h3 className="font-display font-semibold text-gray-900 text-lg mb-2">
        {challenge.title}
      </h3>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
        {challenge.description}
      </p>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple-200 to-transparent mb-4" />

      {/* Solution */}
      <div className="flex items-start gap-2">
        <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm font-medium text-purple-900">
          {challenge.solution}
        </p>
      </div>
    </div>
  );
}

export default function InvestorsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setIsVisible(true);
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="investors"
      ref={sectionRef}
      className="relative py-16 lg:py-20 overflow-hidden"
      style={{
        background:
          'linear-gradient(180deg, hsl(var(--warm-white)) 0%, hsl(40 30% 97%) 50%, hsl(var(--warm-white)) 100%)',
      }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute w-[600px] h-[600px] rounded-full opacity-20"
          style={{
            top: '10%',
            right: '-10%',
            background:
              'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            bottom: '20%',
            left: '-10%',
            background:
              'radial-gradient(circle, rgba(251, 146, 60, 0.15) 0%, transparent 70%)',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <div
            className={cn(
              'transition-all duration-700',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            )}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-100 mb-4">
              <TrendingUp className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700 uppercase tracking-wider">
                For Capital Partners
              </span>
            </div>

            <h2
              className="font-display font-semibold text-gray-900 mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
            >
              Invest with{' '}
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                Confidence & Leverage
              </span>
            </h2>

            <p className="text-lg text-gray-600 leading-relaxed">
              Purple Venture Studio solves the core challenges capital investors face—
              from overwhelming deal flow to parallel diligence constraints. Get structured
              research, independent evaluation, and on-demand expertise.
            </p>
          </div>
        </div>

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12">
          {investorChallenges.map((challenge, index) => (
            <ChallengeCard
              key={challenge.title}
              challenge={challenge}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Value Proposition */}
        <div
          className={cn(
            'max-w-4xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 transition-all duration-700 delay-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <div className="text-center">
            <h3 className="font-display font-semibold text-gray-900 text-2xl mb-4">
              What You Get as a Capital Partner
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-3xl font-display font-bold text-amber-600 mb-1">
                  Investor Thesis
                </div>
                <p className="text-sm text-gray-600">Structured frameworks for decision-making</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-display font-bold text-amber-600 mb-1">
                  Structured Diligence
                </div>
                <p className="text-sm text-gray-600">Expert-led independent analysis</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-display font-bold text-amber-600 mb-1">
                  Curated Deal Flow
                </div>
                <p className="text-sm text-gray-600">Pre-vetted, high-signal opportunities</p>
              </div>
            </div>

            {/* CTA */}
            <a href="/apply/investor">
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all group"
              >
                <span className="flex items-center gap-2">
                  Apply as Capital Investor
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Button>
            </a>
          </div>
        </div>

        {/* Bottom quote */}
        <div
          className={cn(
            'text-center mt-12 transition-all duration-700 delay-700',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          )}
        >
          <p className="text-lg text-gray-500 font-serif italic max-w-2xl mx-auto">
            "Investment Capital & Fees for Investor Thesis, Structured Diligence & Curated Deal Flow"
          </p>
        </div>
      </div>
    </section>
  );
}
